import React, { useState } from 'react';
import { Modal, Button, Card, Container, Row, Col, Badge } from 'react-bootstrap';
import { 
  FaWhatsapp, 
  FaEnvelope, 
  FaCode, 
  FaPalette, 
  FaArrowRight,
  FaChevronRight,
  FaTag,
  FaUsers,
  FaRocket,
  FaCheckCircle,
  FaStar,
  FaCrown} from 'react-icons/fa';
import './Ratecard.css';

const RateCard = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({ 
    title: '', 
    description: '',
    price: '',
    features: []
  });

  // Detailed service data with pricing
  const services = [
    {
      id: 1,
      title: "Graphic Design",
      icon: <FaPalette className="service-icon" />,
      color: "#f59e0b",
      items: [
        {
          name: "Logo Design",
          description: "Custom logo tailored to your brand identity with multiple concepts and revisions.",
          price: "KES 3,500",
          features: ["3 initial concepts", "Unlimited revisions", "Color variations", "Final files in all formats"],
          delivery: "3-5 business days"
        },
        {
          name: "Flyers & Posters",
          description: "Eye-catching marketing materials designed for print and digital distribution.",
          price: "KES 1,000",
          features: ["Print-ready design", "A4/A5 sizes", "Source files included", "Social media adaptation"],
          delivery: "1-2 business days"
        },
        {
          name: "Business Cards",
          description: "Professional business card design with front and back layouts.",
          price: "KES 500",
          features: ["Front & back design", "Bleed included", "High-resolution files", "Print consultation"],
          delivery: "1 business day"
        },
        {
          name: "Social Media Posts",
          description: "Custom branded designs optimized for Instagram, Facebook, and Twitter.",
          price: "KES 600",
          features: ["Platform-optimized", "Brand consistency", "Caption suggestions", "Hashtag research"],
          delivery: "1 business day"
        }
      ],
      addons: [
        "Linktree setup – KES 800",
        "Story Templates – KES 400 each",
        "Facebook/Instagram Ads Setup – From KES 2,000",
        "WhatsApp Business Catalog Setup – KES 2,000"
      ]
    },
    {
      id: 2,
      title: "Web Development",
      icon: <FaCode className="service-icon" />,
      color: "#3b82f6",
      items: [
        {
          name: "One-Page Website",
          description: "Scrollable landing page with contact form, optimized for conversions.",
          price: "KES 10,000",
          features: ["Mobile responsive", "Contact form", "SEO optimized", "1 month support"],
          delivery: "3-5 business days"
        },
        {
          name: "Business Website",
          description: "Professional multi-page website with CMS for easy content management.",
          price: "KES 30,000 - 50,000",
          features: ["5-12 pages", "CMS integration", "Google Analytics", "3 months support"],
          delivery: "2-3 weeks"
        },
        {
          name: "E-commerce Website",
          description: "Complete online store with product management and secure payments.",
          price: "From KES 50,000",
          features: ["Product catalog", "Secure checkout", "Payment gateway", "6 months support"],
          delivery: "3-4 weeks"
        },
        {
          name: "Custom Software",
          description: "Tailored solutions for hospitals, pharmacies, POS, booking systems, etc.",
          price: "From KES 80,000",
          features: ["Custom features", "User management", "Database setup", "1 year support"],
          delivery: "4-8 weeks"
        }
      ],
      maintenance: [
        "Website Maintenance – From KES 5,000/month",
        "Domain & Hosting Setup – From KES 5,000",
        "Email Integration – KES 1,000"
      ]
    },
    {
      id: 3,
      title: "Social Media Management",
      icon: <FaUsers className="service-icon" />,
      color: "#8b5cf6",
      packages: [
        {
          name: "Starter",
          price: "KES 15,000/month",
          features: ["12 posts/week", "Captions & hashtags", "Page monitoring", "Basic reporting"],
          bestFor: "Small businesses starting on social media"
        },
        {
          name: "Growth",
          price: "KES 25,000/month",
          features: ["20 posts + 2 reels/week", "Hashtag strategy", "Insights report", "Community engagement"],
          bestFor: "Businesses wanting steady growth",
          popular: true
        },
        {
          name: "Pro",
          price: "KES 35,000+/month",
          features: ["30 posts + 4 reels/week", "Story management", "DMs/comments handling", "Full analytics & ads"],
          bestFor: "Established brands wanting domination"
        }
      ]
    }
  ];

  const openModal = (serviceTitle, item) => {
    setModalContent({
      title: item.name,
      description: item.description,
      price: item.price,
      features: item.features || [],
      delivery: item.delivery || '',
      bestFor: item.bestFor || ''
    });
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  return (
    <div className="ratecard-page">
      {/* Hero Section */}
      <section className="ratecard-hero">
        <Container>
          <div className="hero-content">
            <Badge bg="light" text="dark" className="hero-badge">
              <FaTag className="me-2" /> Transparent Pricing
            </Badge>
            <h1>Shyee Codes Rate Card</h1>
            <p className="hero-subtitle">
              Professional digital solutions with clear, upfront pricing. 
              All services tailored for Kenyan businesses.
            </p>
            <div className="cta-buttons">
              <Button 
                variant="primary" 
                size="lg"
                href="https://wa.me/254762272152"
                target="_blank"
                className="cta-button"
              >
                <FaWhatsapp className="me-2" /> Get Instant Quote
              </Button>
              <Button 
                variant="outline-light" 
                size="lg"
                href="#services"
                className="cta-button"
              >
                View Services <FaArrowRight className="ms-2" />
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* Pricing Tiers Overview */}
      <section className="tiers-overview py-5">
        <Container>
          <div className="text-center mb-5">
            <h2>Choose Your Perfect Package</h2>
            <p className="text-muted">Solutions for every stage of your digital journey</p>
          </div>
          <Row className="g-4">
            <Col lg={4}>
              <Card className="tier-card h-100">
                <Card.Header className="tier-header">
                  <FaPalette className="tier-icon" style={{color: '#f59e0b'}} />
                  <h3>Design Essentials</h3>
                  <div className="price-range">KES 500 - 3,500</div>
                </Card.Header>
                <Card.Body>
                  <ul className="tier-features">
                    <li><FaCheckCircle className="me-2" /> Logo & Branding</li>
                    <li><FaCheckCircle className="me-2" /> Marketing Materials</li>
                    <li><FaCheckCircle className="me-2" /> Social Media Graphics</li>
                    <li><FaCheckCircle className="me-2" /> Quick Turnaround</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="tier-card h-100 popular">
                <Card.Header className="tier-header popular">
                  <FaCode className="tier-icon" style={{color: '#3b82f6'}} />
                  <h3>Web Solutions</h3>
                  <div className="price-range">KES 10,000 - 80,000+</div>
                  <Badge bg="warning" className="popular-badge">
                    <FaStar className="me-1" /> Most Popular
                  </Badge>
                </Card.Header>
                <Card.Body>
                  <ul className="tier-features">
                    <li><FaCheckCircle className="me-2" /> Responsive Websites</li>
                    <li><FaCheckCircle className="me-2" /> E-commerce Stores</li>
                    <li><FaCheckCircle className="me-2" /> Custom Software</li>
                    <li><FaCheckCircle className="me-2" /> Ongoing Support</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="tier-card h-100">
                <Card.Header className="tier-header">
                  <FaUsers className="tier-icon" style={{color: '#8b5cf6'}} />
                  <h3>Growth Packages</h3>
                  <div className="price-range">KES 15,000 - 35,000+/mo</div>
                </Card.Header>
                <Card.Body>
                  <ul className="tier-features">
                    <li><FaCheckCircle className="me-2" /> Social Media Management</li>
                    <li><FaCheckCircle className="me-2" /> Content Strategy</li>
                    <li><FaCheckCircle className="me-2" /> Performance Analytics</li>
                    <li><FaCheckCircle className="me-2" /> Ad Campaign Management</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Detailed Services Section */}
      <section className="detailed-services py-5" id="services">
        <Container>
          {services.map((service) => (
            <div key={service.id} className="service-category mb-5">
              <div className="category-header mb-4">
                <div className="category-icon" style={{backgroundColor: `${service.color}20`}}>
                  {service.icon}
                </div>
                <div>
                  <h2>{service.title}</h2>
                  <p className="text-muted">Professional solutions with transparent pricing</p>
                </div>
              </div>

              {/* For Graphic Design & Web Dev */}
              {service.items && (
                <Row className="g-4">
                  {service.items.map((item, index) => (
                    <Col md={6} lg={3} key={index}>
                      <Card className="service-item-card h-100">
                        <Card.Body>
                          <div className="price-badge" style={{backgroundColor: service.color}}>
                            {item.price}
                          </div>
                          <h5 className="mt-3">{item.name}</h5>
                          <p className="text-muted small">{item.description.substring(0, 80)}...</p>
                          <ul className="feature-list small">
                            {item.features.slice(0, 2).map((feature, i) => (
                              <li key={i}>{feature}</li>
                            ))}
                          </ul>
                          <div className="text-center mt-3">
                            <Button 
                              variant="link" 
                              className="detail-link"
                              onClick={() => openModal(service.title, item)}
                            >
                              View Details <FaChevronRight className="ms-1" />
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}

              {/* For Social Media Packages */}
              {service.packages && (
                <Row className="g-4">
                  {service.packages.map((pkg, index) => (
                    <Col md={4} key={index}>
                      <Card className={`package-card h-100 ${pkg.popular ? 'popular-package' : ''}`}>
                        {pkg.popular && (
                          <div className="package-badge">
                            <FaCrown className="me-1" /> Most Popular
                          </div>
                        )}
                        <Card.Header className="package-header">
                          <h4>{pkg.name}</h4>
                          <div className="package-price">{pkg.price}</div>
                          <small className="text-muted">{pkg.bestFor}</small>
                        </Card.Header>
                        <Card.Body>
                          <ul className="package-features">
                            {pkg.features.map((feature, i) => (
                              <li key={i}>
                                <FaCheckCircle className="me-2 text-success" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </Card.Body>
                        <Card.Footer className="text-center">
                          <Button 
                            variant={pkg.popular ? "primary" : "outline-primary"}
                            href="https://wa.me/254762272152"
                            target="_blank"
                            className="w-100"
                          >
                            <FaWhatsapp className="me-2" /> Choose {pkg.name}
                          </Button>
                        </Card.Footer>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}

              {/* Add-ons Section */}
              {(service.addons || service.maintenance) && (
                <div className="addons-section mt-4">
                  <h5 className="mb-3">
                    <FaRocket className="me-2" />
                    Additional Services
                  </h5>
                  <Row>
                    <Col md={6}>
                      <ul className="addons-list">
                        {(service.addons || []).map((addon, index) => (
                          <li key={index}>{addon}</li>
                        ))}
                      </ul>
                    </Col>
                    <Col md={6}>
                      <ul className="addons-list">
                        {(service.maintenance || []).map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </Col>
                  </Row>
                </div>
              )}
            </div>
          ))}

        </Container>
      </section>

  

      {/* Service Modal */}
      <Modal 
        show={showModal} 
        onHide={closeModal} 
        centered
        size="lg"
        className="service-detail-modal"
      >
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title>{modalContent.title}</Modal.Title>
          <div className="modal-price">{modalContent.price}</div>
        </Modal.Header>
        <Modal.Body>
          <p className="modal-description">{modalContent.description}</p>
          
          {modalContent.features.length > 0 && (
            <div className="modal-features mt-4">
              <h6>What's Included:</h6>
              <Row>
                {modalContent.features.map((feature, index) => (
                  <Col md={6} key={index}>
                    <div className="feature-item">
                      <FaCheckCircle className="me-2 text-success" />
                      <span>{feature}</span>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {(modalContent.delivery || modalContent.bestFor) && (
            <div className="modal-details mt-4">
              <Row>
                {modalContent.delivery && (
                  <Col md={6}>
                    <div className="detail-item">
                      <strong>Delivery Time:</strong>
                      <div>{modalContent.delivery}</div>
                    </div>
                  </Col>
                )}
                {modalContent.bestFor && (
                  <Col md={6}>
                    <div className="detail-item">
                      <strong>Best For:</strong>
                      <div>{modalContent.bestFor}</div>
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="flex-column">
          <p className="mb-3 text-center">
            Ready to proceed or need customization?
          </p>
          <div className="w-100 d-flex gap-3">
            <Button 
              variant="success" 
              href="https://wa.me/254762272152"
              target="_blank"
              className="flex-grow-1"
            >
              <FaWhatsapp className="me-2" /> WhatsApp Order
            </Button>
            <Button 
              variant="primary"
              href="mailto:hello@shyeecodes.com?subject=Quote Request"
              className="flex-grow-1"
            >
              <FaEnvelope className="me-2" /> Email Inquiry
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RateCard;